// gazePatternTracker — detects TWO distinct cheating-relevant eye-gaze
// patterns that look very different in time-series shape.
//
//  Pattern A: SUSTAINED off-screen gaze
//  ──────────────────────────────────────
//  Candidate stares at the hidden phone / second monitor / notes for
//  N+ continuous seconds. Classic "memorize the answer" cheating.
//  Detected by: continuous off-screen time exceeds warnAfterMs / flagAfterMs.
//
//  Pattern B: REPEATED brief glances (the one the simple state machine missed)
//  ──────────────────────────────────────────────────────────────────────────
//  Candidate places phone in the corner of the laptop and rapidly cycles:
//     look-at-phone (0.5-1.5s) → look-at-editor → type → look-at-phone →
//     look-at-editor → type → repeat
//  Each individual glance is BELOW the sustained-gaze threshold, but the
//  pattern repeats 5-10 times in a minute. No legitimate scanning behaviour
//  looks like this — humans reading their own screen don't return to the
//  same off-screen point repeatedly.
//  Detected by: counting glances in a sliding window.
//
// Both patterns share the same feature flag (`mediapipe.eye_gaze`); this
// module just emits different event payloads so the recruiter dashboard
// can show which pattern fired.
//
// Tunable thresholds (all overridable via the feature's threshold JSON):
//   sustained_warn_seconds   - default 2s
//   sustained_flag_seconds   - default 5s
//   repeat_window_seconds    - default 60s  (sliding-window size)
//   min_glance_ms            - default 300  (saccades shorter than this are ignored)
//   max_glance_ms            - default 5000 (anything longer = sustained, not a "glance")
//   glance_count_warn        - default 3    (3 glances in window → warn)
//   glance_count_flag        - default 5    (5+ glances in window → flag)

export function createGazePatternTracker({
  warnAfterMs = 2000,
  flagAfterMs = 5000,
  windowMs = 60000,
  minGlanceMs = 300,
  maxGlanceMs = 5000,
  glanceCountWarn = 3,
  glanceCountFlag = 5,
  onSustainedWarn = () => {},
  onSustainedFlag = () => {},
  onRepeatedWarn = () => {},
  onRepeatedFlag = () => {},
  onClear = () => {},
} = {}) {
  // ── Pattern A state: ongoing off-screen episode ─────────────────────────
  let offScreenStart = null;
  let sustainedWarnedThisEpisode = false;
  let sustainedFlaggedThisEpisode = false;

  // ── Pattern B state: sliding window of completed brief glances ──────────
  /** @type {{time: number, duration: number}[]} */
  let glanceEvents = [];

  // Debounce so the repeated-glance handlers don't fire on every tick once
  // the count crosses the threshold — we want one event per "episode" of
  // repeated glances, then quiet until the situation materially changes.
  let repeatedWarnedAt = 0;
  let repeatedFlaggedAt = 0;
  const REPEAT_REFIRE_MS = windowMs / 2; // half a window = sensible re-fire gap

  const reset = () => {
    const wasInEpisode = offScreenStart !== null;
    offScreenStart = null;
    sustainedWarnedThisEpisode = false;
    sustainedFlaggedThisEpisode = false;
    if (wasInEpisode) {
      try { onClear(); } catch (_) { /* keep ticking */ }
    }
  };

  const update = (isOff) => {
    const now = Date.now();

    if (isOff) {
      // ── Enter or continue an off-screen episode ──────────────────────────
      if (offScreenStart === null) {
        offScreenStart = now;
        sustainedWarnedThisEpisode = false;
        sustainedFlaggedThisEpisode = false;
      } else {
        const elapsed = now - offScreenStart;
        if (elapsed >= warnAfterMs && !sustainedWarnedThisEpisode) {
          sustainedWarnedThisEpisode = true;
          try { onSustainedWarn(); } catch (_) { /* keep ticking */ }
        }
        if (elapsed >= flagAfterMs && !sustainedFlaggedThisEpisode) {
          sustainedFlaggedThisEpisode = true;
          try { onSustainedFlag(); } catch (_) { /* keep ticking */ }
        }
      }
    } else {
      // ── Just returned to on-screen? Close the episode ────────────────────
      if (offScreenStart !== null) {
        const duration = now - offScreenStart;
        // Only count it as a "glance" if it was in the brief range. Tiny
        // saccades (< minGlanceMs) are normal eye movement; long stares
        // (> maxGlanceMs) belong to Pattern A's bookkeeping, not B's.
        if (duration >= minGlanceMs && duration <= maxGlanceMs) {
          glanceEvents.push({ time: now, duration });
        }
        offScreenStart = null;
        sustainedWarnedThisEpisode = false;
        sustainedFlaggedThisEpisode = false;
      }
    }

    // ── Prune glance events outside the sliding window ───────────────────
    // Done on every tick (cheap — usually 0-10 entries) so the count
    // reflects the latest windowMs of history without needing a separate
    // timer.
    const cutoff = now - windowMs;
    if (glanceEvents.length > 0 && glanceEvents[0].time < cutoff) {
      glanceEvents = glanceEvents.filter((g) => g.time >= cutoff);
    }

    // ── Pattern B: check sliding-window count ─────────────────────────────
    const count = glanceEvents.length;
    if (count >= glanceCountFlag) {
      if (now - repeatedFlaggedAt > REPEAT_REFIRE_MS) {
        repeatedFlaggedAt = now;
        repeatedWarnedAt = now;
        try {
          onRepeatedFlag({
            count,
            windowSeconds: Math.round(windowMs / 1000),
            glances: glanceEvents.slice(),
          });
        } catch (_) { /* keep ticking */ }
      }
    } else if (count >= glanceCountWarn) {
      if (now - repeatedWarnedAt > REPEAT_REFIRE_MS) {
        repeatedWarnedAt = now;
        try {
          onRepeatedWarn({
            count,
            windowSeconds: Math.round(windowMs / 1000),
            glances: glanceEvents.slice(),
          });
        } catch (_) { /* keep ticking */ }
      }
    }
  };

  return {
    update,
    reset,
    /** Snapshot of internal state — useful for tests / debug overlays. */
    inspect: () => ({
      inEpisode: offScreenStart !== null,
      episodeMs: offScreenStart === null ? 0 : Date.now() - offScreenStart,
      glanceCount: glanceEvents.length,
      glances: glanceEvents.slice(),
    }),
  };
}
